import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Building2, Loader2, ShieldCheck, AlertCircle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAFF_ROLES = ["admin", "super_admin", "doctor", "lab_staff", "radiology_staff", "receptionist", "content_moderator", "content_manager"];

export default function CorporateEnquiryForm({ tiers, selectedTierId, onTierChange }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    company_name: "", industry: "", hr_name: user?.name || "", hr_email: user?.email || "", hr_phone: user?.phone || "",
    headcount_estimate: 25, gstin: "", address: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (!user) {
    return (
      <div className="p-6 bg-[#EBF4F1] border border-emerald-200 rounded-3xl text-center space-y-3" data-testid="corporate-signin-required">
        <Building2 className="w-8 h-8 text-[#0D3B2E] mx-auto" />
        <p className="text-sm text-gray-800">Sign in with your 6SigmaCare patient account to open a corporate wellness account for your company.</p>
        <div className="flex flex-wrap justify-center gap-2">
          <button data-testid="corporate-signin-btn" onClick={() => navigate("/login", { state: { from: "/corporate" } })} className="px-5 py-2.5 bg-[#0D3B2E] text-white text-xs font-semibold rounded-xl inline-flex items-center gap-2">Sign In <ArrowRight className="w-3.5 h-3.5" /></button>
          <button data-testid="corporate-register-btn" onClick={() => navigate("/register")} className="px-5 py-2.5 border border-[#0D3B2E] text-[#0D3B2E] text-xs font-semibold rounded-xl">Create Account</button>
        </div>
      </div>
    );
  }
  if (user?.corporate_id) {
    return (
      <div className="p-6 bg-[#EBF4F1] border border-emerald-200 rounded-3xl text-center space-y-3" data-testid="corporate-already-linked">
        <ShieldCheck className="w-8 h-8 text-[#0D3B2E] mx-auto" />
        <p className="text-sm text-gray-800">You already manage <b>{user.company_name}</b>.</p>
        <button data-testid="corporate-goto-portal-btn" onClick={() => navigate("/corporate/portal")} className="px-5 py-2.5 bg-[#0D3B2E] text-white text-xs font-semibold rounded-xl inline-flex items-center gap-2">
          Open HR Portal <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  if (STAFF_ROLES.includes(user.role)) {
    return <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl text-xs text-amber-900" data-testid="corporate-staff-note">Admin accounts cannot open a corporate account. Please sign in with a patient account using the HR's work email.</div>;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!selectedTierId) { setError("Please choose a wellness tier"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/corporate/enquiry`, {
        company_name: form.company_name, industry: form.industry, hr_name: form.hr_name, hr_phone: form.hr_phone,
        tier_id: selectedTierId, headcount_estimate: Number(form.headcount_estimate), gstin: form.gstin, address: form.address,
      }, { withCredentials: true });
      navigate("/corporate/portal");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not submit enquiry");
    } finally {
      setSubmitting(false);
    }
  };

  const input = "w-full px-3 py-2.5 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-[#0D3B2E]";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="corporate-enquiry-form">
      {error && <div data-testid="corporate-enquiry-error" className="p-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-xl flex items-center gap-2"><AlertCircle className="w-4 h-4 shrink-0" />{error}</div>}
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="block text-xs font-semibold text-gray-700 mb-1">Company Name *</label><input required value={form.company_name} onChange={set("company_name")} data-testid="corp-company-name" className={input} placeholder="Acme Technologies Pvt Ltd" /></div>
        <div><label className="block text-xs font-semibold text-gray-700 mb-1">Industry</label><input value={form.industry} onChange={set("industry")} data-testid="corp-industry" className={input} placeholder="IT / BFSI / Manufacturing" /></div>
        <div><label className="block text-xs font-semibold text-gray-700 mb-1">HR Contact Name *</label><input required value={form.hr_name} onChange={set("hr_name")} data-testid="corp-hr-name" className={input} /></div>
        <div><label className="block text-xs font-semibold text-gray-700 mb-1">HR Work Email *</label><input required type="email" disabled value={form.hr_email} onChange={set("hr_email")} data-testid="corp-hr-email" className={input} /></div>
        <div><label className="block text-xs font-semibold text-gray-700 mb-1">HR Phone *</label><input required value={form.hr_phone} onChange={set("hr_phone")} data-testid="corp-hr-phone" className={input} placeholder="+91 98300 00000" /></div>
        <div><label className="block text-xs font-semibold text-gray-700 mb-1">Estimated Headcount *</label><input required type="number" min={1} value={form.headcount_estimate} onChange={set("headcount_estimate")} data-testid="corp-headcount" className={input} /></div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Wellness Tier *</label>
          <select value={selectedTierId} onChange={(e) => onTierChange(e.target.value)} data-testid="corp-tier-select" className={input}>
            <option value="">Select a tier</option>
            {tiers.map((t) => <option key={t.id} value={t.id}>{`${t.name} — ₹${t.price_per_employee}/employee`}</option>)}
          </select>
        </div>
        <div><label className="block text-xs font-semibold text-gray-700 mb-1">GSTIN (for invoice)</label><input value={form.gstin} onChange={set("gstin")} data-testid="corp-gstin" className={`${input} uppercase font-mono`} placeholder="19ABCDE1234F1Z5" /></div>
        <div className="sm:col-span-2"><label className="block text-xs font-semibold text-gray-700 mb-1">Registered Office Address</label><input value={form.address} onChange={set("address")} data-testid="corp-address" className={input} placeholder="Sector V, Salt Lake, Kolkata 700091" /></div>
      </div>
      <button type="submit" disabled={submitting} data-testid="corp-enquiry-submit-btn" className="w-full py-3.5 bg-[#0D3B2E] hover:bg-[#165644] text-white text-xs font-semibold rounded-xl flex items-center justify-center gap-2 transition-colors">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4 text-amber-300" />}
        Open Corporate Account & HR Portal
      </button>
    </form>
  );
}
